'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Cookie, X, Settings, Check, ChevronDown, ChevronUp } from 'lucide-react';

type CookiePrefs = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = 'wealthos_cookie_consent';

const translations = {
  en: {
    title: 'We use cookies',
    desc: 'We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts. You can manage your preferences below.',
    necessary: 'Necessary',
    necessaryDesc: 'Required for the website to function. Cannot be disabled.',
    analytics: 'Analytics',
    analyticsDesc: 'Help us understand how visitors interact with our website.',
    marketing: 'Marketing',
    marketingDesc: 'Used to deliver relevant advertisements and track campaigns.',
    acceptAll: 'Accept All',
    rejectAll: 'Reject Non-Essential',
    savePrefs: 'Save Preferences',
    customize: 'Customize',
    learnMore: 'Privacy Policy',
    close: 'Close cookie banner',
  },
  ar: {
    title: 'نستخدم ملفات تعريف الارتباط',
    desc: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتحليل استخدام الموقع ودعم جهودنا التسويقية. يمكنك إدارة تفضيلاتك أدناه.',
    necessary: 'ضرورية',
    necessaryDesc: 'مطلوبة لعمل الموقع. لا يمكن تعطيلها.',
    analytics: 'تحليلية',
    analyticsDesc: 'تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا.',
    marketing: 'تسويقية',
    marketingDesc: 'تُستخدم لتقديم إعلانات ذات صلة وتتبع الحملات.',
    acceptAll: 'قبول الكل',
    rejectAll: 'رفض غير الضرورية',
    savePrefs: 'حفظ التفضيلات',
    customize: 'تخصيص',
    learnMore: 'سياسة الخصوصية',
    close: 'إغلاق إشعار الكوكيز',
  },
  fr: {
    title: 'Nous utilisons des cookies',
    desc: 'Nous utilisons des cookies pour améliorer votre expérience, analyser l\'utilisation du site et soutenir nos efforts marketing. Vous pouvez gérer vos préférences ci-dessous.',
    necessary: 'Nécessaires',
    necessaryDesc: 'Requis pour le fonctionnement du site. Ne peut pas être désactivé.',
    analytics: 'Analytiques',
    analyticsDesc: 'Nous aident à comprendre comment les visiteurs interagissent avec notre site.',
    marketing: 'Marketing',
    marketingDesc: 'Utilisés pour diffuser des publicités pertinentes et suivre les campagnes.',
    acceptAll: 'Tout accepter',
    rejectAll: 'Refuser le non-essentiel',
    savePrefs: 'Enregistrer les préférences',
    customize: 'Personnaliser',
    learnMore: 'Politique de confidentialité',
    close: 'Fermer la bannière de cookies',
  },
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lang, setLang] = useState<'en' | 'ar' | 'fr'>('en');
  const [prefs, setPrefs] = useState<CookiePrefs>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Detect language from document dir or html lang
      const htmlLang = document.documentElement.lang;
      if (htmlLang === 'ar') setLang('ar');
      else if (htmlLang === 'fr') setLang('fr');
      setTimeout(() => setVisible(true), 1500);
    }
  }, []);

  const t = translations[lang];

  const handleAcceptAll = () => {
    const allPrefs: CookiePrefs = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allPrefs));
    setVisible(false);
  };

  const handleRejectAll = () => {
    const minPrefs: CookiePrefs = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(minPrefs));
    setVisible(false);
  };

  const handleSavePrefs = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setVisible(false);
  };

  if (!visible) return null;

  const isRtl = lang === 'ar';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t.title}
      dir={isRtl ? 'rtl' : 'ltr'}
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[9998] animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="bg-card border border-border rounded-2xl shadow-2xl shadow-black/30 overflow-hidden">
        {/* Header */}
        <div className={`flex items-start justify-between gap-3 p-4 pb-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Cookie size={16} className="text-primary" />
            </div>
            <h2 className="text-sm font-bold text-foreground">{t.title}</h2>
          </div>
          <div className={`flex items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
            {(['en', 'ar', 'fr'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all ${
                  lang === l ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                aria-label={`Switch to ${l.toUpperCase()}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
            <button
              onClick={() => setVisible(false)}
              aria-label={t.close}
              className="ml-1 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 pb-3">
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">{t.desc}</p>

          {/* Expanded preferences */}
          {expanded && (
            <div className="space-y-2 mb-3 border border-border/50 rounded-xl p-3 bg-muted/20">
              {/* Necessary */}
              <div className={`flex items-start justify-between gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <div>
                  <p className="text-xs font-semibold text-foreground">{t.necessary}</p>
                  <p className="text-[11px] text-muted-foreground">{t.necessaryDesc}</p>
                </div>
                <div className="w-8 h-5 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={10} className="text-primary" />
                </div>
              </div>
              {/* Analytics */}
              <div className={`flex items-start justify-between gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <div>
                  <p className="text-xs font-semibold text-foreground">{t.analytics}</p>
                  <p className="text-[11px] text-muted-foreground">{t.analyticsDesc}</p>
                </div>
                <button
                  role="switch"
                  aria-checked={prefs.analytics}
                  onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                  className={`relative w-8 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${
                    prefs.analytics ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      prefs.analytics ? (isRtl ? 'translate-x-[-14px]' : 'translate-x-[14px]') : (isRtl ? 'translate-x-[-2px]' : 'translate-x-[2px]')
                    }`}
                  />
                </button>
              </div>
              {/* Marketing */}
              <div className={`flex items-start justify-between gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <div>
                  <p className="text-xs font-semibold text-foreground">{t.marketing}</p>
                  <p className="text-[11px] text-muted-foreground">{t.marketingDesc}</p>
                </div>
                <button
                  role="switch"
                  aria-checked={prefs.marketing}
                  onClick={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                  className={`relative w-8 h-5 rounded-full transition-colors flex-shrink-0 mt-0.5 ${
                    prefs.marketing ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      prefs.marketing ? (isRtl ? 'translate-x-[-14px]' : 'translate-x-[14px]') : (isRtl ? 'translate-x-[-2px]' : 'translate-x-[2px]')
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Privacy link */}
          <Link href="/privacy" className="text-[11px] text-primary hover:underline">
            {t.learnMore}
          </Link>
        </div>

        {/* Actions */}
        <div className={`flex flex-col gap-2 px-4 pb-4 ${isRtl ? 'items-end' : ''}`}>
          <div className={`flex gap-2 w-full ${isRtl ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-3 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition-colors"
            >
              {t.acceptAll}
            </button>
            <button
              onClick={handleRejectAll}
              className="flex-1 px-3 py-2 rounded-xl border border-border text-foreground text-xs font-semibold hover:bg-muted/50 transition-colors"
            >
              {t.rejectAll}
            </button>
          </div>
          <div className={`flex gap-2 w-full ${isRtl ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => setExpanded(!expanded)}
              className={`flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
            >
              <Settings size={11} />
              {t.customize}
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
            {expanded && (
              <button
                onClick={handleSavePrefs}
                className="ml-auto text-[11px] text-primary font-semibold hover:underline"
              >
                {t.savePrefs}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
